import { useOperationIntent } from "@/lib/data/react-query/connections/use-operation-intent";
import {
  createVehicleContactConnectionInput,
  type SupportedVehicleContactRole,
} from "@/lib/utils/connections/vehicle-contact-connections";
import {
  LIEN_CONTACT_CONNECTION_TYPES,
  type SupportedLienContactRole,
} from "@/lib/utils/connections/lien-contact-connections";
import { createContactConnectionInput } from "@/lib/utils/connections/item-contact-connections";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  checkMatterContactConflict,
  retrieveContactMattersV1,
} from "@/lib/data/requests/matters/matter-contact-connection";
import {
  createConnectionV4,
  updateConnectionV4,
  deleteConnectionV4,
  replayPendingConnectionV4,
} from "@/lib/data/requests/connections/connection";
import { queryKeys } from "@/lib/data/static";
import type { Contact, MatterContactConnection } from "@/lib/types";
import { findOwnedConnection } from "@/lib/utils/connections/find-owned-connection";
import {
  createMatterPartyConnectionInput,
  isSupportedMatterPartyRole,
} from "@/lib/utils/connections/matter-party-connections";
import { invalidateSearchQueries } from "@/lib/data/react-query/search/invalidation";
import toast from "react-hot-toast";

type Options = {
  showToast?: boolean;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
};
export const retrieveContactMattersQueryOptions = (contactId?: string) => ({
  queryKey: [queryKeys.matters, "contacts", contactId, "v4"],
  enabled: !!contactId,
  queryFn: async () => retrieveContactMattersV1(contactId),
});
const requireConnection = (
  client: ReturnType<typeof useQueryClient>,
  id: string,
) => {
  for (const [, value] of client.getQueriesData({
    queryKey: [queryKeys.matters],
  })) {
    const connection = findOwnedConnection(value, id);
    if (connection) return connection;
  }
  throw new Error("Reload the owning object before editing this connection.");
};
function useConnectionOptions(options: Options, action: string) {
  const client = useQueryClient();
  const intent = useOperationIntent();
  return {
    ...intent,
    onError: (error: unknown, variables: { operationId?: string }) => {
      intent.onError(error, variables);
      if (options.showErrorToast ?? options.showToast ?? true)
        toast.error(`Error ${action} connection`);
    },
    onSuccess: (data: unknown, variables: { operationId?: string }) => {
      intent.onSuccess(data, variables);
      if (options.showSuccessToast ?? options.showToast ?? true)
        toast.success("Connection saved successfully");
    },
    onSettled: async () => {
      await invalidateSearchQueries(client);
      await client.invalidateQueries({ queryKey: [queryKeys.matters] });
      await client.invalidateQueries({ queryKey: [queryKeys.contact] });
    },
  };
}
export const useCreateMatterContactConnection = (options: Options = {}) =>
  useMutation({
    mutationFn: (
      data: Pick<
        MatterContactConnection,
        "matterId" | "contactId" | "label"
      > & { contact?: Partial<Contact> } & Partial<MatterContactConnection> & {
          operationId?: string;
        },
    ) => {
      if (data.vehicleId)
        return createConnectionV4({
          ...createVehicleContactConnectionInput(
            data.vehicleId,
            data.contactId,
            data.label as SupportedVehicleContactRole,
          ),
          matterId: data.matterId,
          operationId: data.operationId,
          description: data.description,
        });
      if (data.expenseId)
        return createConnectionV4({
          ...createContactConnectionInput(
            data.expenseId,
            "matter_expense",
            data.contactId,
            "matter_expense_to_payee",
          ),
          matterId: data.matterId,
          operationId: data.operationId,
          description: data.description,
        });
      if (data.lienId)
        return createConnectionV4({
          ...createContactConnectionInput(
            data.lienId,
            "matter_lien",
            data.contactId,
            LIEN_CONTACT_CONNECTION_TYPES[
              data.label as SupportedLienContactRole
            ],
          ),
          matterId: data.matterId,
          operationId: data.operationId,
          description: data.description,
        });
      if (!isSupportedMatterPartyRole(data.label))
        throw new Error("Select a supported matter role.");
      return createConnectionV4({
        ...createMatterPartyConnectionInput({ ...data, role: data.label }),
        matterId: data.matterId,
        operationId: data.operationId,
      });
    },
    ...useConnectionOptions(options, "creating"),
  });
export const useUpdateMatterContactConnection = (options: Options = {}) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Pick<MatterContactConnection, "dateModified" | "id" | "matterId"> &
        Partial<MatterContactConnection> & { operationId?: string },
    ) => {
      const retry = replayPendingConnectionV4(data.operationId);
      if (retry) return retry;
      const connection = requireConnection(client, data.id);
      return updateConnectionV4(
        connection.id,
        {
          operation: "update",
          operationId: data.operationId,
          dateModified: data.dateModified,
          description: data.description,
          dateTimeStart: data.dateTimeStart,
          dateTimeEnd: data.dateTimeEnd,
          faultStatus: data.faultStatus,
          insuranceCoverage: data.insuranceCoverage,
          witnessStatus: data.witnessStatus,
        },
        { ...connection, matterId: data.matterId },
      );
    },
    ...useConnectionOptions(options, "updating"),
  });
};
export const useDeleteMatterContactConnection = (options: Options = {}) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      id: string;
      matterId?: string;
      operationId?: string;
    }) =>
      replayPendingConnectionV4(data.operationId) ??
      deleteConnectionV4(
        requireConnection(client, data.id),
        data.matterId,
        data.operationId,
      ),
    ...useConnectionOptions(options, "removing"),
  });
};

export const useCheckMatterContactConflict = () =>
  useMutation({
    mutationFn: checkMatterContactConflict,
    onError: () => {
      toast.error("Unable to check for conflicts. Please try again.");
    },
  });
